import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react"

const STORAGE_KEY = "device-simulator"
const SIMULATOR_COUNT = 3

export interface SimulatorConfig {
  apiToken: string
  frequency: string
  enabled: boolean
}

export type SimulatorStatus = "idle" | "success" | "error"

export interface SimulatorState extends SimulatorConfig {
  status: SimulatorStatus
  lastError?: string
  postCount: number
}

interface SimulatorContextValue {
  simulators: SimulatorState[]
  updateSimulator: (id: number, updates: Partial<SimulatorConfig>) => void
  getSimulator: (id: number) => SimulatorState
}

const SimulatorContext = createContext<SimulatorContextValue | null>(null)

function getStoredSettings(): Record<number, SimulatorConfig> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch {
    // ignore
  }
  return {}
}

function saveSettings(settings: Record<number, SimulatorConfig>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // ignore
  }
}

function generateRandomReading() {
  return {
    reading: {
      specific_gravity: 1.0 + Math.random() * 0.1,
      temperature: 60 + Math.random() * 20,
      tilt_angle: Math.random() * 90,
      battery_voltage: 2.5 + Math.random() * 1.5,
      accelerometer: {
        x: Math.round((Math.random() - 0.5) * 2000),
        y: Math.round((Math.random() - 0.5) * 2000),
        z: Math.round((Math.random() - 0.5) * 2000 + 1000), // Bias toward gravity
      },
      pth: {
        temp: 20 + Math.random() * 15, // 20-35°C
        pressure: 1000 + Math.random() * 30, // 1000-1030 hPa
        humidity: 40 + Math.random() * 40, // 40-80%
      },
    },
  }
}

async function postReading(apiToken: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const response = await fetch("/api/v1/device_readings", {
      method: "POST",
      headers: {
        Authorization: `Token token=${apiToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(generateRandomReading()),
    })
    if (response.ok) {
      return { ok: true }
    }
    const text = await response.text()
    return { ok: false, error: `${response.status}: ${text}` }
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Network error" }
  }
}

function createInitialState(): SimulatorState[] {
  const stored = getStoredSettings()
  return Array.from({ length: SIMULATOR_COUNT }, (_, i) => {
    const id = i + 1
    const config = stored[id] || { apiToken: "", frequency: "1", enabled: false }
    return {
      ...config,
      status: "idle" as SimulatorStatus,
      postCount: 0,
    }
  })
}

interface SimulatorProviderProps {
  children: ReactNode
}

export function SimulatorProvider({ children }: SimulatorProviderProps) {
  const [simulators, setSimulators] = useState<SimulatorState[]>(createInitialState)
  const intervalsRef = useRef<Map<number, number>>(new Map())

  // Save settings to localStorage when config changes
  useEffect(() => {
    const settings: Record<number, SimulatorConfig> = {}
    simulators.forEach((sim, i) => {
      const id = i + 1
      settings[id] = {
        apiToken: sim.apiToken,
        frequency: sim.frequency,
        enabled: sim.enabled,
      }
    })
    saveSettings(settings)
  }, [simulators])

  // Manage intervals for each simulator
  useEffect(() => {
    simulators.forEach((sim, index) => {
      const id = index + 1
      const existingInterval = intervalsRef.current.get(id)

      if (sim.enabled && sim.apiToken) {
        // Clear existing interval if frequency changed
        if (existingInterval !== undefined) {
          window.clearInterval(existingInterval)
          intervalsRef.current.delete(id)
        }

        const intervalMs = 1000 / parseFloat(sim.frequency)

        const doPost = async () => {
          const result = await postReading(sim.apiToken)
          setSimulators((prev) =>
            prev.map((s, i) =>
              i === index
                ? {
                    ...s,
                    status: result.ok ? "success" : "error",
                    lastError: result.ok ? undefined : result.error,
                    postCount: result.ok ? s.postCount + 1 : s.postCount,
                  }
                : s
            )
          )
        }

        // Post immediately and then at interval
        doPost()
        const intervalId = window.setInterval(doPost, intervalMs)
        intervalsRef.current.set(id, intervalId)
      } else {
        // Clear interval if disabled
        if (existingInterval !== undefined) {
          window.clearInterval(existingInterval)
          intervalsRef.current.delete(id)
        }
      }
    })

    // Cleanup on unmount
    return () => {
      intervalsRef.current.forEach((intervalId) => {
        window.clearInterval(intervalId)
      })
      intervalsRef.current.clear()
    }
  }, [simulators.map((s) => `${s.enabled}-${s.apiToken}-${s.frequency}`).join(",")])

  const updateSimulator = useCallback((id: number, updates: Partial<SimulatorConfig>) => {
    setSimulators((prev) =>
      prev.map((sim, i) => {
        if (i + 1 !== id) return sim
        const newSim = { ...sim, ...updates }
        // Reset status and count when toggling off
        if (updates.enabled === false) {
          newSim.status = "idle"
          newSim.postCount = 0
        }
        return newSim
      })
    )
  }, [])

  const getSimulator = useCallback(
    (id: number): SimulatorState => {
      return simulators[id - 1] || {
        apiToken: "",
        frequency: "1",
        enabled: false,
        status: "idle",
        postCount: 0,
      }
    },
    [simulators]
  )

  return (
    <SimulatorContext.Provider value={{ simulators, updateSimulator, getSimulator }}>
      {children}
    </SimulatorContext.Provider>
  )
}

export function useSimulator() {
  const context = useContext(SimulatorContext)
  if (!context) {
    throw new Error("useSimulator must be used within a SimulatorProvider")
  }
  return context
}
